if (!customElements.get('sandluz-sticky-buy')) {
  customElements.define(
    'sandluz-sticky-buy',
    class SandluzStickyBuy extends HTMLElement {
      connectedCallback() {
        this.mode = this.dataset.purchaseMode || 'cart_and_checkout';
        this.mainProduct = this.closest('product-info');
        this.canonicalInfo = this.mainProduct?.querySelector('[id^="ProductInfo-"]');
        this.canonicalForm = document.getElementById(this.dataset.productFormId);
        this.primaryCheckout = this.canonicalForm?.querySelector('[data-direct-checkout-primary]');
        this.structuralSubmit = this.canonicalForm?.querySelector('[data-direct-checkout-structural-submit]');
        this.stickyPrice = this.querySelector('[data-sticky-price]');
        this.stickyError = this.querySelector('[data-sticky-error]');
        this.sync = this.sync.bind(this);
        this.handleCanonicalField = this.handleCanonicalField.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);

        if (!this.mainProduct || !this.canonicalInfo || !this.canonicalForm) return;

        if (this.mode === 'checkout_only') {
          this.connectDirectCheckout();
        } else {
          this.connectCartCheckout();
        }

        this.observeFooter();
        this.protectedCarouselControls = Array.from(
          document.querySelectorAll(
            'sandluz-video-carousel [data-carousel-toggle], sandluz-video-carousel [data-carousel-prev], sandluz-video-carousel [data-carousel-next], sandluz-video-carousel [data-carousel-dot]'
          )
        );
        window.addEventListener('scroll', this.handleViewportChange, { passive: true });
        window.addEventListener('resize', this.handleViewportChange);
        this.updateProtectedControlOverlap();
        this.sync();
      }

      handleViewportChange() {
        if (this.viewportFrame) return;
        this.viewportFrame = window.requestAnimationFrame(() => {
          this.viewportFrame = null;
          this.updateProtectedControlOverlap();
          this.setVisibility(Boolean(this.primaryPassed));
        });
      }

      getProspectiveStickyRect() {
        const width = this.offsetWidth;
        const height = this.offsetHeight;
        const floatingDesktop = window.matchMedia('(min-width: 990px)').matches;
        const bottomGap = floatingDesktop ? Number.parseFloat(getComputedStyle(this).bottom) || 0 : 0;
        const left = floatingDesktop ? (window.innerWidth - width) / 2 : 0;
        const bottom = window.innerHeight - bottomGap;

        return {
          top: bottom - height,
          right: left + width,
          bottom,
          left,
        };
      }

      updateProtectedControlOverlap() {
        if (!this.protectedCarouselControls?.length) {
          this.controlsObscured = false;
          return;
        }

        const stickyRect = this.getProspectiveStickyRect();
        this.controlsObscured = this.protectedCarouselControls.some((control) => {
          if (control.hidden || control.closest('[inert]')) return false;
          const controlRect = control.getBoundingClientRect();
          if (controlRect.width === 0 || controlRect.height === 0) return false;

          return (
            controlRect.bottom > stickyRect.top &&
            controlRect.top < stickyRect.bottom &&
            controlRect.right > stickyRect.left &&
            controlRect.left < stickyRect.right
          );
        });
      }

      connectDirectCheckout() {
        this.stickyCheckout = this.querySelector('[data-sticky-checkout]');
        this.stickyForm = this.stickyCheckout?.querySelector('form');
        if (!this.primaryCheckout || !this.structuralSubmit || !this.stickyForm) return;

        this.checkoutReady = false;
        this.filePropertyPresent = this.hasFileProperty();
        this.mainProduct.addEventListener('input', this.handleCanonicalField);
        this.mainProduct.addEventListener('change', this.handleCanonicalField);

        this.mainObserver = new MutationObserver(this.sync);
        this.mainObserver.observe(this.structuralSubmit, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });

        this.stickyCheckoutObserver = new MutationObserver(() => this.updateStickyCheckoutReady());
        this.stickyCheckoutObserver.observe(this.stickyForm, { childList: true, subtree: true });
        this.updateStickyCheckoutReady();
        this.checkoutTimeout = window.setTimeout(() => {
          if (!this.checkoutReady) this.setVisibility(false);
        }, 8000);

        this.stickyForm.addEventListener('pointerdown', this.sync, true);
        this.stickyForm.addEventListener('keydown', this.sync, true);
        this.observePrimaryButton(this.primaryCheckout);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(this.sync));
          this.quantityUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => requestAnimationFrame(this.sync));
        }
      }

      connectCartCheckout() {
        this.stickyButton = this.querySelector('[data-sticky-submit]');
        this.stickyLabel = this.querySelector('[data-sticky-label]');
        if (!this.stickyButton) return;

        this.mainObserver = new MutationObserver(this.sync);
        const primaryButton = this.getCartPrimaryButton();
        if (primaryButton) {
          this.mainObserver.observe(primaryButton, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class'],
          });
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            this.clearError();
            requestAnimationFrame(this.sync);
          });
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
            if (!this.matchesProductEvent(event) || this.dataset.visible !== 'true') return;
            const message = typeof event.errors === 'string' ? event.errors : event.message;
            this.showError(message);
            requestAnimationFrame(this.sync);
          });
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (!this.matchesProductEvent(event)) return;
            this.clearError();
            requestAnimationFrame(this.sync);
          });
        }
      }

      handleCanonicalField(event) {
        if (event.target?.form !== this.canonicalForm) return;
        this.filePropertyPresent = this.hasFileProperty();
        this.sync();
      }

      hasFileProperty() {
        return Array.from(this.canonicalForm?.elements || []).some(
          (field) =>
            field instanceof HTMLInputElement &&
            field.type === 'file' &&
            field.name.startsWith('properties[')
        );
      }

      syncStickyForm() {
        if (!this.stickyForm || !this.canonicalForm) return;

        const variantSource = this.canonicalForm.elements.namedItem('id');
        const stickyVariant = this.stickyForm.querySelector('[data-sticky-variant-id]');
        const variantValue = variantSource?.value || '';
        const variantDisabled = !variantValue || this.isUnavailable();
        const mirroredFields = [];
        const allowedName = (name) =>
          name === 'quantity' ||
          name === 'selling_plan' ||
          name.startsWith('properties[') ||
          name.startsWith('items[') ||
          name.startsWith('shopify-additional-items[');

        Array.from(this.canonicalForm.elements).forEach((field) => {
          if (!field.name || !allowedName(field.name) || field.disabled || field.type === 'file') return;
          if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;

          const values =
            field instanceof HTMLSelectElement && field.multiple
              ? Array.from(field.selectedOptions).map((option) => option.value)
              : [field.value];

          values.forEach((value) => {
            if (field.name !== 'quantity' && value === '') return;
            mirroredFields.push({ name: field.name, value: value || '1' });
          });
        });

        if (!mirroredFields.some((field) => field.name === 'quantity')) {
          mirroredFields.push({ name: 'quantity', value: '1' });
        }

        const nextSignature = JSON.stringify({
          variantValue,
          variantDisabled,
          mirroredFields,
        });
        if (nextSignature === this.formSignature) return;
        this.formSignature = nextSignature;

        this.stickyForm.querySelectorAll('[data-sticky-mirror]').forEach((field) => field.remove());
        mirroredFields.forEach((field) => {
          const mirror = document.createElement('input');
          mirror.type = 'hidden';
          mirror.name = field.name;
          mirror.value = field.value;
          mirror.dataset.stickyMirror = 'true';
          this.stickyForm.appendChild(mirror);
        });
        this.stickyForm.querySelector('[data-sticky-quantity]')?.remove();

        if (stickyVariant) {
          const variantChanged = stickyVariant.value !== variantValue || stickyVariant.disabled !== variantDisabled;
          stickyVariant.value = variantValue;
          stickyVariant.disabled = variantDisabled;
          if (variantChanged) stickyVariant.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      updateStickyCheckoutReady() {
        if (!this.stickyForm) return;
        const acceleratedCheckout = this.stickyForm.querySelector(
          'shopify-accelerated-checkout, .shopify-payment-button__button'
        );
        const wasReady = this.checkoutReady;
        this.checkoutReady = Boolean(acceleratedCheckout);
        const moreOptions = this.stickyForm.querySelector('.shopify-payment-button__more-options');
        if (moreOptions) {
          moreOptions.hidden = true;
          moreOptions.tabIndex = -1;
          moreOptions.setAttribute('aria-hidden', 'true');
        }
        this.dataset.checkoutReady = this.checkoutReady.toString();
        if (!this.checkoutReady) {
          this.setVisibility(false);
        } else if (!wasReady) {
          window.clearTimeout(this.checkoutTimeout);
          requestAnimationFrame(this.sync);
        }
      }

      isUnavailable() {
        return (
          !this.structuralSubmit ||
          this.structuralSubmit.disabled ||
          this.structuralSubmit.getAttribute('aria-disabled') === 'true' ||
          this.primaryCheckout?.hidden
        );
      }

      observePrimaryButton(primaryButton) {
        if (!primaryButton || primaryButton === this.observedPrimaryButton) return;

        this.visibilityObserver?.disconnect();
        if (this.fallbackVisibilityHandler) {
          window.removeEventListener('scroll', this.fallbackVisibilityHandler);
          window.removeEventListener('resize', this.fallbackVisibilityHandler);
        }
        this.observedPrimaryButton = primaryButton;

        if (!('IntersectionObserver' in window)) {
          this.fallbackVisibilityHandler = () => {
            const shouldShow = primaryButton.getBoundingClientRect().bottom <= 0;
            this.setPrimaryVisibility(shouldShow, primaryButton);
          };
          window.addEventListener('scroll', this.fallbackVisibilityHandler, { passive: true });
          window.addEventListener('resize', this.fallbackVisibilityHandler);
          this.fallbackVisibilityHandler();
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            const shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.setPrimaryVisibility(shouldShow, primaryButton);
          },
          { threshold: 0.15 }
        );
        this.visibilityObserver.observe(primaryButton);
      }

      setPrimaryVisibility(visible, primaryButton = this.observedPrimaryButton) {
        this.primaryPassed = visible;
        this.setVisibility(visible, primaryButton);
      }

      observeFooter() {
        const footer = document.querySelector('footer');
        if (!footer || !('IntersectionObserver' in window)) return;

        this.footerObserver = new IntersectionObserver(
          ([entry]) => {
            this.footerVisible = entry.isIntersecting;
            this.setVisibility(Boolean(this.primaryPassed));
          },
          { threshold: 0 }
        );
        this.footerObserver.observe(footer);
      }

      setVisibility(visible, primaryButton = this.observedPrimaryButton) {
        const canShow =
          visible &&
          !this.footerVisible &&
          !this.controlsObscured &&
          !this.isUnavailable() &&
          !this.filePropertyPresent &&
          (this.mode !== 'checkout_only' || this.checkoutReady);

        if (!canShow && this.contains(document.activeElement)) {
          const mainCheckout = primaryButton?.querySelector('shopify-accelerated-checkout');
          mainCheckout?.focus({ preventScroll: true });
        }

        this.dataset.visible = canShow.toString();
        this.setAttribute('aria-hidden', (!canShow).toString());
        this.inert = !canShow;

        if (this.stickyButton) this.stickyButton.tabIndex = canShow ? 0 : -1;
        if (!canShow) this.clearError();
      }

      getCartPrimaryButton() {
        return this.canonicalInfo.querySelector('product-form .product-form__submit:not([hidden])');
      }

      matchesProductEvent(event) {
        const variantId = this.canonicalForm?.querySelector('[name="id"]')?.value;
        return event?.source === 'product-form' && event.productVariantId?.toString() === variantId;
      }

      showError(message) {
        if (!message || !this.stickyError) return;
        this.stickyError.textContent = message;
        this.stickyError.hidden = false;
      }

      clearError() {
        if (!this.stickyError) return;
        this.stickyError.hidden = true;
        this.stickyError.textContent = '';
      }

      syncPrice() {
        const priceContainer = this.canonicalInfo.querySelector('[id^="price-"]');
        const sourcePrice = priceContainer?.querySelector('.price');
        const priceHidden = !priceContainer || priceContainer.classList.contains('hidden');

        if (!this.stickyPrice) return;
        this.stickyPrice.hidden = priceHidden;
        this.stickyPrice.innerHTML = !priceHidden && sourcePrice ? sourcePrice.outerHTML : '';
      }

      sync() {
        this.syncPrice();

        if (this.mode === 'checkout_only') {
          if (!this.stickyForm || !this.primaryCheckout) return;
          this.filePropertyPresent = this.hasFileProperty();
          this.syncStickyForm();
          this.observePrimaryButton(this.primaryCheckout);
          if (this.isUnavailable() || this.filePropertyPresent || !this.checkoutReady) {
            this.setVisibility(false);
          } else if (this.primaryPassed) {
            this.setVisibility(true);
          }
          return;
        }

        const primaryButton = this.getCartPrimaryButton();
        if (!primaryButton || !this.stickyButton) return;
        this.observePrimaryButton(primaryButton);

        const loading = primaryButton.classList.contains('loading');
        const disabled = primaryButton.disabled || primaryButton.getAttribute('aria-disabled') === 'true';
        const sourceLabel = primaryButton.querySelector('span:not(.sold-out-message)');

        this.stickyButton.disabled = disabled;
        this.stickyButton.classList.toggle('loading', loading);
        this.stickyButton.setAttribute('aria-busy', loading.toString());
        this.stickyButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        if (sourceLabel && this.stickyLabel) this.stickyLabel.textContent = sourceLabel.textContent.trim();
      }

      disconnectedCallback() {
        window.clearTimeout(this.checkoutTimeout);
        window.cancelAnimationFrame(this.viewportFrame);
        window.removeEventListener('scroll', this.handleViewportChange);
        window.removeEventListener('resize', this.handleViewportChange);
        this.mainObserver?.disconnect();
        this.stickyCheckoutObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.footerObserver?.disconnect();
        if (this.fallbackVisibilityHandler) {
          window.removeEventListener('scroll', this.fallbackVisibilityHandler);
          window.removeEventListener('resize', this.fallbackVisibilityHandler);
        }
        this.mainProduct?.removeEventListener('input', this.handleCanonicalField);
        this.mainProduct?.removeEventListener('change', this.handleCanonicalField);
        this.stickyForm?.removeEventListener('pointerdown', this.sync, true);
        this.stickyForm?.removeEventListener('keydown', this.sync, true);
        this.variantUnsubscriber?.();
        this.quantityUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }
    }
  );
}
